import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from './firebase';
import * as XLSX from 'xlsx';
import './HOTshirtInvoiceReport.css';

function HOTshirtInvoiceReport({ onClose }) {
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState([]);
  const [batches, setBatches] = useState([]);
  const [selectedBatch, setSelectedBatch] = useState('all');
  const [feeStructure, setFeeStructure] = useState(null);

  const kidsFullNames = [
    '2-3 YEARS', '4-5 YEARS', '5-6 YEARS', '7-8 YEARS',
    '9-10 YEARS', '11-12 YEARS', '13-14 YEARS'
  ];
  const roundNeckAdult = ['S', 'M', 'L'];
  const allRoundNeck = [...kidsFullNames, ...roundNeckAdult];

  const kidsSizeHeaders = ['2-3 Yrs', '4-5 Yrs', '5-6 Yrs', '7-8 Yrs', '9-10 Yrs', '11-12 Yrs', '13-14 Yrs'];

  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      await Promise.all([loadOrders(), loadBatches(), loadFeeStructure()]);
    } catch (e) {
      console.error('Error loading data:', e);
      alert('Error loading data: ' + e.message);
    } finally {
      setLoading(false);
    }
  };

  const loadFeeStructure = async () => {
    try {
      const docSnap = await getDoc(doc(db, 'adminFeeStructure', 'current'));
      if (docSnap.exists()) setFeeStructure(docSnap.data());
    } catch (e) {
      console.error('Error loading fee structure:', e);
    }
  };

  const loadBatches = async () => {
    try {
      const snapshot = await getDocs(collection(db, 'supplierBatches'));
      const list = snapshot.docs.map(d => {
        const data = d.data();
        return {
          id: d.id, ...data,
          startDateParsed: data.startDate?.toDate ? data.startDate.toDate() : new Date(data.startDate),
          endDateParsed: data.endDate?.toDate ? data.endDate.toDate() : new Date(data.endDate),
        };
      });
      list.sort((a, b) => b.startDateParsed - a.startDateParsed);
      setBatches(list);
      if (list.length > 0) setSelectedBatch(list[0].id);
    } catch (e) {
      console.error('Error loading batches:', e);
    }
  };

  const loadOrders = async () => {
    try {
      const snapshot = await getDocs(collection(db, 'tshirtOrders'));
      const list = snapshot.docs.map(d => {
        const data = d.data();
        const orderDate = data.orderDate?.toDate
          ? data.orderDate.toDate()
          : data.orderDate ? new Date(data.orderDate) : null;
        return { id: d.id, ...data, orderDateParsed: orderDate };
      });
      list.sort((a, b) => (a.franchiseeName || '').localeCompare(b.franchiseeName || ''));
      setOrders(list);
    } catch (e) {
      console.error('Error loading orders:', e);
      throw e;
    }
  };

  const isOrderInBatch = (order, batch) => {
    if (!order.orderDateParsed) return false;
    const t = order.orderDateParsed.getTime();
    const s = new Date(batch.startDateParsed); s.setHours(0, 0, 0, 0);
    const e = new Date(batch.endDateParsed); e.setHours(23, 59, 59, 999);
    return t >= s.getTime() && t <= e.getTime();
  };

  const getFilteredOrders = () => {
    if (selectedBatch === 'all') return orders;
    const batch = batches.find(b => b.id === selectedBatch);
    if (!batch) return orders;
    return orders.filter(o => isOrderInBatch(o, batch));
  };

  const normalizeSize = s => (s || '').toUpperCase().trim();

  const getBatchLabel = () => {
    if (selectedBatch === 'all') return 'All Orders';
    return batches.find(b => b.id === selectedBatch)?.name || 'Unknown';
  };

  // Build per-franchisee invoice data
  const buildInvoiceData = () => {
    const filtered = getFilteredOrders();
    const byFranchisee = {};

    filtered.forEach(order => {
      const name = order.franchiseeName || 'Unknown';
      if (!byFranchisee[name]) byFranchisee[name] = { name, orders: [] };
      byFranchisee[name].orders.push(order);
    });

    const KIDS_PRICE = feeStructure?.headOfficeGets || 120;

    return Object.values(byFranchisee)
      .sort((a, b) => a.name.localeCompare(b.name))
      .map(f => {
        const kidsSizes = {};
        const vneckSML = { S: 0, M: 0, L: 0 };
        const vneckXL = { XL: 0, XXL: 0, XXXL: 0 };
        let stickersQty = 0;
        let vneckSMLPrice = 0;
        let vneckXLPrice = 0;
        let stickerPrice = 0;

        f.orders.forEach(order => {
          // Student shirts
          (order.studentTshirts || []).forEach(st => {
            if (st.size) {
              const key = normalizeSize(st.size);
              kidsSizes[key] = (kidsSizes[key] || 0) + 1;
            }
          });
          // Extra stock (round neck only)
          if (order.extraStock) {
            Object.entries(order.extraStock).forEach(([size, qty]) => {
              if ((qty || 0) > 0) {
                const key = normalizeSize(size);
                kidsSizes[key] = (kidsSizes[key] || 0) + qty;
              }
            });
          }
          // V-neck S/M/L
          const slSizes = order.adultTshirts?.smallToLarge?.sizes || {};
          vneckSML.S += slSizes.S || 0;
          vneckSML.M += slSizes.M || 0;
          vneckSML.L += slSizes.L || 0;
          if (order.adultTshirts?.smallToLarge?.priceEach) {
            vneckSMLPrice = order.adultTshirts.smallToLarge.priceEach;
          }
          // V-neck XL-XXXL
          const xlSizes = order.adultTshirts?.xlToXxxxl?.sizes || {};
          vneckXL.XL += xlSizes.XL || 0;
          vneckXL.XXL += xlSizes.XXL || 0;
          vneckXL.XXXL += xlSizes.XXXL || 0;
          if (order.adultTshirts?.xlToXxxxl?.priceEach) {
            vneckXLPrice = order.adultTshirts.xlToXxxxl.priceEach;
          }
          // Stickers
          stickersQty += order.stickers?.quantity || 0;
          if (order.stickers?.pricePerRoll) {
            stickerPrice = order.stickers.pricePerRoll;
          }
        });

        // Fallback to fee structure prices
        if (!vneckSMLPrice) vneckSMLPrice = feeStructure?.adultTshirtPricing?.smallToLarge || 120;
        if (!vneckXLPrice) vneckXLPrice = feeStructure?.adultTshirtPricing?.xlToXxxxl || 130;
        if (!stickerPrice) stickerPrice = feeStructure?.stickerPricePerRoll || 70;

        // Totals
        const kidsTotal = allRoundNeck.reduce((sum, s) => sum + (kidsSizes[normalizeSize(s)] || 0), 0);
        const kidsAmt = kidsTotal * KIDS_PRICE;
        const vneckSMLTotal = vneckSML.S + vneckSML.M + vneckSML.L;
        const vneckSMLAmt = vneckSMLTotal * vneckSMLPrice;
        const vneckXLTotal = vneckXL.XL + vneckXL.XXL + vneckXL.XXXL;
        const vneckXLAmt = vneckXLTotal * vneckXLPrice;
        const stickerAmt = stickersQty * stickerPrice;
        const invoiceTotal = kidsAmt + vneckSMLAmt + vneckXLAmt + stickerAmt;

        return {
          name: f.name,
          orderCount: f.orders.length,
          orders: f.orders,
          kidsSizes,
          kidsTotal,
          kidsAmt,
          KIDS_PRICE,
          vneckSML,
          vneckSMLTotal,
          vneckSMLAmt,
          vneckSMLPrice,
          vneckXL,
          vneckXLTotal,
          vneckXLAmt,
          vneckXLPrice,
          stickersQty,
          stickerAmt,
          stickerPrice,
          invoiceTotal
        };
      });
  };

  const formatCurrency = amt => `R ${Number(amt || 0).toFixed(2)}`;
  const formatDate = date => {
    if (!date) return 'N/A';
    return date.toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  // ─── EXCEL DOWNLOAD ───────────────────────────────────────────────

  const downloadExcelAll = () => {
    const invoiceData = buildInvoiceData();
    if (invoiceData.length === 0) { alert('No data to export.'); return; }

    const wb = XLSX.utils.book_new();
    const batchLabel = getBatchLabel();
    const dateStr = new Date().toLocaleDateString('en-ZA');

    // ── SUMMARY SHEET ──
    const summaryRows = [
      ['BEAUX-ARTS KIDZ KRAFTZ - T-SHIRT INVOICE REPORT'],
      [`Generated: ${dateStr}`],
      [`Supplier Order: ${batchLabel}`],
      [`Total Franchisees: ${invoiceData.length}`],
      [],
      [
        'Franchisee', 'Orders',
        ...kidsSizeHeaders, 'S', 'M', 'L',
        `Kids Total`, `Kids × R${invoiceData[0]?.KIDS_PRICE || 120}`,
        'V-S', 'V-M', 'V-L', 'VNeck S-L Total', `VNeck S-L ×R${invoiceData[0]?.vneckSMLPrice || 120}`,
        'V-XL', 'V-XXL', 'V-XXXL', 'VNeck XL Total', `VNeck XL ×R${invoiceData[0]?.vneckXLPrice || 130}`,
        'Stickers', `Sticker ×R${invoiceData[0]?.stickerPrice || 70}`,
        'INVOICE TOTAL'
      ]
    ];

    let grandKids = 0, grandKidsAmt = 0;
    let grandVSML = 0, grandVSMLAmt = 0;
    let grandVXL = 0, grandVXLAmt = 0;
    let grandStickers = 0, grandStickerAmt = 0;
    let grandInvoice = 0;
    const grandKidsSizes = {};
    const grandVSMLSizes = { S: 0, M: 0, L: 0 };
    const grandVXLSizes = { XL: 0, XXL: 0, XXXL: 0 };

    invoiceData.forEach(f => {
      summaryRows.push([
        f.name, f.orderCount,
        f.kidsSizes['2-3 YEARS'] || 0,
        f.kidsSizes['4-5 YEARS'] || 0,
        f.kidsSizes['5-6 YEARS'] || 0,
        f.kidsSizes['7-8 YEARS'] || 0,
        f.kidsSizes['9-10 YEARS'] || 0,
        f.kidsSizes['11-12 YEARS'] || 0,
        f.kidsSizes['13-14 YEARS'] || 0,
        f.kidsSizes['S'] || 0,
        f.kidsSizes['M'] || 0,
        f.kidsSizes['L'] || 0,
        f.kidsTotal, f.kidsAmt,
        f.vneckSML.S, f.vneckSML.M, f.vneckSML.L, f.vneckSMLTotal, f.vneckSMLAmt,
        f.vneckXL.XL, f.vneckXL.XXL, f.vneckXL.XXXL, f.vneckXLTotal, f.vneckXLAmt,
        f.stickersQty, f.stickerAmt,
        f.invoiceTotal
      ]);
      grandKids += f.kidsTotal; grandKidsAmt += f.kidsAmt;
      grandVSML += f.vneckSMLTotal; grandVSMLAmt += f.vneckSMLAmt;
      grandVXL += f.vneckXLTotal; grandVXLAmt += f.vneckXLAmt;
      grandStickers += f.stickersQty; grandStickerAmt += f.stickerAmt;
      grandInvoice += f.invoiceTotal;
      kidsFullNames.forEach(s => { grandKidsSizes[s] = (grandKidsSizes[s] || 0) + (f.kidsSizes[normalizeSize(s)] || 0); });
      roundNeckAdult.forEach(s => { grandKidsSizes[s] = (grandKidsSizes[s] || 0) + (f.kidsSizes[normalizeSize(s)] || 0); });
      grandVSMLSizes.S += f.vneckSML.S; grandVSMLSizes.M += f.vneckSML.M; grandVSMLSizes.L += f.vneckSML.L;
      grandVXLSizes.XL += f.vneckXL.XL; grandVXLSizes.XXL += f.vneckXL.XXL; grandVXLSizes.XXXL += f.vneckXL.XXXL;
    });

    summaryRows.push([]);
    summaryRows.push([
      'TOTALS', invoiceData.reduce((s, f) => s + f.orderCount, 0),
      grandKidsSizes['2-3 YEARS'] || 0,
      grandKidsSizes['4-5 YEARS'] || 0,
      grandKidsSizes['5-6 YEARS'] || 0,
      grandKidsSizes['7-8 YEARS'] || 0,
      grandKidsSizes['9-10 YEARS'] || 0,
      grandKidsSizes['11-12 YEARS'] || 0,
      grandKidsSizes['13-14 YEARS'] || 0,
      grandKidsSizes['S'] || 0,
      grandKidsSizes['M'] || 0,
      grandKidsSizes['L'] || 0,
      grandKids, grandKidsAmt,
      grandVSMLSizes.S, grandVSMLSizes.M, grandVSMLSizes.L, grandVSML, grandVSMLAmt,
      grandVXLSizes.XL, grandVXLSizes.XXL, grandVXLSizes.XXXL, grandVXL, grandVXLAmt,
      grandStickers, grandStickerAmt,
      grandInvoice
    ]);

    const wsSummary = XLSX.utils.aoa_to_sheet(summaryRows);
    wsSummary['!cols'] = [
      { wch: 25 }, { wch: 8 },
      ...Array(10).fill({ wch: 8 }),
      { wch: 12 }, { wch: 15 },
      { wch: 6 }, { wch: 6 }, { wch: 6 }, { wch: 14 }, { wch: 16 },
      { wch: 6 }, { wch: 7 }, { wch: 8 }, { wch: 13 }, { wch: 16 },
      { wch: 9 }, { wch: 14 },
      { wch: 15 }
    ];
    XLSX.utils.book_append_sheet(wb, wsSummary, 'Invoice Summary');

    // ── PER-FRANCHISEE SHEETS ──
    invoiceData.forEach(f => {
      const rows = buildFranchiseeSheetRows(f, batchLabel, dateStr);
      const ws = XLSX.utils.aoa_to_sheet(rows);
      ws['!cols'] = [{ wch: 20 }, { wch: 10 }, { wch: 10 }, { wch: 15 }];
      const sheetName = f.name.substring(0, 31).replace(/[/\\?*[\]:]/g, '_');
      XLSX.utils.book_append_sheet(wb, ws, sheetName);
    });

    XLSX.writeFile(wb, `TShirt_Invoice_${batchLabel.replace(/\s+/g, '_')}_${dateStr}.xlsx`);
  };

  const downloadExcelSingle = (franchiseeData) => {
    const wb = XLSX.utils.book_new();
    const batchLabel = getBatchLabel();
    const dateStr = new Date().toLocaleDateString('en-ZA');

    const rows = buildFranchiseeSheetRows(franchiseeData, batchLabel, dateStr);
    const ws = XLSX.utils.aoa_to_sheet(rows);
    ws['!cols'] = [{ wch: 22 }, { wch: 10 }, { wch: 12 }, { wch: 16 }];
    XLSX.utils.book_append_sheet(wb, ws, 'Invoice');

    const filename = `Invoice_${franchiseeData.name.replace(/\s+/g, '_')}_${batchLabel.replace(/\s+/g, '_')}_${dateStr}.xlsx`;
    XLSX.writeFile(wb, filename);
  };

  const buildFranchiseeSheetRows = (f, batchLabel, dateStr) => {
    const rows = [
      ['BEAUX-ARTS KIDZ KRAFTZ'],
      ['T-SHIRT INVOICE'],
      [],
      ['Franchisee:', f.name],
      ['Supplier Order:', batchLabel],
      ['Generated:', dateStr],
      ['Number of Orders:', f.orderCount],
      [],
      // Order numbers listed
      ['Orders included:'],
      ...f.orders.map(o => [`  ${o.orderNumber || 'N/A'}`, formatDate(o.orderDateParsed)]),
      [],
      // ── SECTION 1: Kids T-shirts ──
      [`KIDS T-SHIRTS (Round Neck) @ R${f.KIDS_PRICE} each`],
      ['Size', 'Qty'],
      ['2-3 Years', f.kidsSizes['2-3 YEARS'] || 0],
      ['4-5 Years', f.kidsSizes['4-5 YEARS'] || 0],
      ['5-6 Years', f.kidsSizes['5-6 YEARS'] || 0],
      ['7-8 Years', f.kidsSizes['7-8 YEARS'] || 0],
      ['9-10 Years', f.kidsSizes['9-10 YEARS'] || 0],
      ['11-12 Years', f.kidsSizes['11-12 YEARS'] || 0],
      ['13-14 Years', f.kidsSizes['13-14 YEARS'] || 0],
      ['S (Round Neck)', f.kidsSizes['S'] || 0],
      ['M (Round Neck)', f.kidsSizes['M'] || 0],
      ['L (Round Neck)', f.kidsSizes['L'] || 0],
      [],
      ['KIDS TOTAL', f.kidsTotal, `× R${f.KIDS_PRICE}`, f.kidsAmt],
      [],
      // ── SECTION 2: V-Neck S/M/L ──
      [`V-NECK SHIRTS (S / M / L) @ R${f.vneckSMLPrice} each`],
      ['Size', 'Qty'],
      ['S', f.vneckSML.S],
      ['M', f.vneckSML.M],
      ['L', f.vneckSML.L],
      [],
      ['V-NECK S-L TOTAL', f.vneckSMLTotal, `× R${f.vneckSMLPrice}`, f.vneckSMLAmt],
      [],
      // ── SECTION 3: V-Neck XL-XXXL ──
      [`V-NECK SHIRTS (XL / XXL / XXXL) @ R${f.vneckXLPrice} each`],
      ['Size', 'Qty'],
      ['XL', f.vneckXL.XL],
      ['XXL', f.vneckXL.XXL],
      ['XXXL', f.vneckXL.XXXL],
      [],
      ['V-NECK XL-XXXL TOTAL', f.vneckXLTotal, `× R${f.vneckXLPrice}`, f.vneckXLAmt],
      [],
      // ── SECTION 4: Stickers ──
      [`STICKERS @ R${f.stickerPrice} per roll`],
      ['Qty', f.stickersQty, `× R${f.stickerPrice}`, f.stickerAmt],
      [],
      [],
      ['─────────────────────────────────────────'],
      ['INVOICE TOTAL', '', '', f.invoiceTotal],
      ['─────────────────────────────────────────'],
    ];
    return rows;
  };

  // ─── RENDER ───────────────────────────────────────────────────────

  const invoiceData = buildInvoiceData();

  // Grand totals
  const grandTotals = invoiceData.reduce((acc, f) => ({
    kids: acc.kids + f.kidsTotal,
    kidsAmt: acc.kidsAmt + f.kidsAmt,
    vsml: acc.vsml + f.vneckSMLTotal,
    vsmlAmt: acc.vsmlAmt + f.vneckSMLAmt,
    vxl: acc.vxl + f.vneckXLTotal,
    vxlAmt: acc.vxlAmt + f.vneckXLAmt,
    stickers: acc.stickers + f.stickersQty,
    stickerAmt: acc.stickerAmt + f.stickerAmt,
    invoice: acc.invoice + f.invoiceTotal,
    orders: acc.orders + f.orderCount,
  }), { kids: 0, kidsAmt: 0, vsml: 0, vsmlAmt: 0, vxl: 0, vxlAmt: 0, stickers: 0, stickerAmt: 0, invoice: 0, orders: 0 });

  if (loading) {
    return (
      <div className="ho-invoice-report">
        <div className="invoice-report-header">
          <button onClick={onClose} className="back-btn">← Back</button>
          <h1>📋 T-Shirt Invoice Report</h1>
        </div>
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading invoice data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="ho-invoice-report">
      {/* ── HEADER ── */}
      <div className="invoice-report-header">
        <button onClick={onClose} className="back-btn">← Back</button>
        <div className="header-title">
          <span className="header-icon">📋</span>
          <div>
            <h1>T-Shirt Invoice Report</h1>
            <p>What to invoice per Franchisee for each Supplier Order</p>
          </div>
        </div>
        <button
          className="download-all-btn"
          onClick={downloadExcelAll}
          disabled={invoiceData.length === 0}
        >
          ⬇ Download Excel (All)
        </button>
      </div>

      {/* ── FILTER BAR ── */}
      <div className="invoice-filter-bar">
        <div className="filter-group">
          <label>Supplier Order / Batch:</label>
          <select
            value={selectedBatch}
            onChange={e => setSelectedBatch(e.target.value)}
          >
            <option value="all">All Orders</option>
            {batches.map(b => (
              <option key={b.id} value={b.id}>
                {b.name} ({b.startDateParsed.toLocaleDateString('en-ZA', { day: 'numeric', month: 'short' })} – {b.endDateParsed.toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' })})
              </option>
            ))}
          </select>
        </div>
        <div className="filter-summary">
          <span className="summary-chip">{invoiceData.length} Franchisees</span>
          <span className="summary-chip">{grandTotals.orders} Orders</span>
          <span className="summary-chip total-chip">Total: {formatCurrency(grandTotals.invoice)}</span>
        </div>
      </div>

      {invoiceData.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📭</div>
          <h3>No orders found</h3>
          <p>No T-shirt orders exist for the selected batch.</p>
        </div>
      ) : (
        <>
          {/* ── SUMMARY CARDS ── */}
          <div className="invoice-summary-cards">
            <div className="summary-card card-blue">
              <div className="sc-label">Kids T-Shirts</div>
              <div className="sc-qty">{grandTotals.kids}</div>
              <div className="sc-amt">{formatCurrency(grandTotals.kidsAmt)}</div>
              <div className="sc-price">@ R{feeStructure?.headOfficeGets || 120} each</div>
            </div>
            <div className="summary-card card-green">
              <div className="sc-label">V-Neck S/M/L</div>
              <div className="sc-qty">{grandTotals.vsml}</div>
              <div className="sc-amt">{formatCurrency(grandTotals.vsmlAmt)}</div>
              <div className="sc-price">@ R{feeStructure?.adultTshirtPricing?.smallToLarge || 120} each</div>
            </div>
            <div className="summary-card card-orange">
              <div className="sc-label">V-Neck XL-XXXL</div>
              <div className="sc-qty">{grandTotals.vxl}</div>
              <div className="sc-amt">{formatCurrency(grandTotals.vxlAmt)}</div>
              <div className="sc-price">@ R{feeStructure?.adultTshirtPricing?.xlToXxxxl || 130} each</div>
            </div>
            <div className="summary-card card-purple">
              <div className="sc-label">Stickers</div>
              <div className="sc-qty">{grandTotals.stickers} rolls</div>
              <div className="sc-amt">{formatCurrency(grandTotals.stickerAmt)}</div>
              <div className="sc-price">@ R{feeStructure?.stickerPricePerRoll || 70} each</div>
            </div>
            <div className="summary-card card-total">
              <div className="sc-label">TOTAL TO INVOICE</div>
              <div className="sc-total-amt">{formatCurrency(grandTotals.invoice)}</div>
              <div className="sc-price">{invoiceData.length} franchisees</div>
            </div>
          </div>

          {/* ── MAIN TABLE ── */}
          <div className="invoice-table-wrapper">
            <table className="invoice-table">
              <thead>
                <tr className="th-group-row">
                  <th rowSpan="2" className="th-franchisee">Franchisee</th>
                  <th rowSpan="2" className="th-orders">Orders</th>
                  {/* Kids sizes */}
                  <th colSpan="10" className="th-group th-group-kids">
                    Kids T-Shirts (Round Neck) @ R{feeStructure?.headOfficeGets || 120} each
                  </th>
                  {/* V-Neck S-L */}
                  <th colSpan="5" className="th-group th-group-vsml">
                    V-Neck S/M/L @ R{feeStructure?.adultTshirtPricing?.smallToLarge || 120} each
                  </th>
                  {/* V-Neck XL */}
                  <th colSpan="5" className="th-group th-group-vxl">
                    V-Neck XL-XXXL @ R{feeStructure?.adultTshirtPricing?.xlToXxxxl || 130} each
                  </th>
                  {/* Stickers */}
                  <th colSpan="2" className="th-group th-group-stickers">
                    Stickers @ R{feeStructure?.stickerPricePerRoll || 70}
                  </th>
                  {/* Invoice Total */}
                  <th rowSpan="2" className="th-invoice-total">INVOICE TOTAL</th>
                  {/* Download */}
                  <th rowSpan="2" className="th-download">Download</th>
                </tr>
                <tr className="th-size-row">
                  {/* Kids sizes */}
                  <th>2-3</th>
                  <th>4-5</th>
                  <th>5-6</th>
                  <th>7-8</th>
                  <th>9-10</th>
                  <th>11-12</th>
                  <th>13-14</th>
                  <th>S</th>
                  <th>M</th>
                  <th className="th-total-col">Total × R{feeStructure?.headOfficeGets || 120}</th>
                  {/* V-Neck S-L */}
                  <th>S</th>
                  <th>M</th>
                  <th>L</th>
                  <th className="th-total-col">Total × R{feeStructure?.adultTshirtPricing?.smallToLarge || 120}</th>
                  <th className="th-amt-col">Amt</th>
                  {/* V-Neck XL */}
                  <th>XL</th>
                  <th>XXL</th>
                  <th>XXXL</th>
                  <th className="th-total-col">Total × R{feeStructure?.adultTshirtPricing?.xlToXxxxl || 130}</th>
                  <th className="th-amt-col">Amt</th>
                  {/* Stickers */}
                  <th>Qty</th>
                  <th className="th-amt-col">Amt</th>
                </tr>
              </thead>
              <tbody>
                {invoiceData.map((f, idx) => (
                  <tr key={f.name} className={idx % 2 === 0 ? 'row-even' : 'row-odd'}>
                    <td className="td-franchisee">{f.name}</td>
                    <td className="td-center">{f.orderCount}</td>
                    {/* Kids sizes */}
                    <td className="td-center">{f.kidsSizes['2-3 YEARS'] || 0}</td>
                    <td className="td-center">{f.kidsSizes['4-5 YEARS'] || 0}</td>
                    <td className="td-center">{f.kidsSizes['5-6 YEARS'] || 0}</td>
                    <td className="td-center">{f.kidsSizes['7-8 YEARS'] || 0}</td>
                    <td className="td-center">{f.kidsSizes['9-10 YEARS'] || 0}</td>
                    <td className="td-center">{f.kidsSizes['11-12 YEARS'] || 0}</td>
                    <td className="td-center">{f.kidsSizes['13-14 YEARS'] || 0}</td>
                    <td className="td-center">{f.kidsSizes['S'] || 0}</td>
                    <td className="td-center">{f.kidsSizes['M'] || 0}</td>
                    <td className="td-total-cell">
                      <span className="qty-badge">{f.kidsTotal}</span>
                      <span className="amt-badge">{formatCurrency(f.kidsAmt)}</span>
                    </td>
                    {/* V-Neck S-L */}
                    <td className="td-center td-vneck">{f.vneckSML.S}</td>
                    <td className="td-center td-vneck">{f.vneckSML.M}</td>
                    <td className="td-center td-vneck">{f.vneckSML.L}</td>
                    <td className="td-total-cell td-vneck">
                      <span className="qty-badge">{f.vneckSMLTotal}</span>
                    </td>
                    <td className="td-amt td-vneck">{formatCurrency(f.vneckSMLAmt)}</td>
                    {/* V-Neck XL */}
                    <td className="td-center td-vxl">{f.vneckXL.XL}</td>
                    <td className="td-center td-vxl">{f.vneckXL.XXL}</td>
                    <td className="td-center td-vxl">{f.vneckXL.XXXL}</td>
                    <td className="td-total-cell td-vxl">
                      <span className="qty-badge">{f.vneckXLTotal}</span>
                    </td>
                    <td className="td-amt td-vxl">{formatCurrency(f.vneckXLAmt)}</td>
                    {/* Stickers */}
                    <td className="td-center td-stickers">{f.stickersQty}</td>
                    <td className="td-amt td-stickers">{formatCurrency(f.stickerAmt)}</td>
                    {/* Invoice Total */}
                    <td className="td-invoice-total">{formatCurrency(f.invoiceTotal)}</td>
                    {/* Download */}
                    <td className="td-download">
                      <button
                        className="btn-download-single"
                        onClick={() => downloadExcelSingle(f)}
                        title={`Download invoice for ${f.name}`}
                      >
                        ⬇ Excel
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="totals-row">
                  <td className="td-franchisee total-label">TOTALS</td>
                  <td className="td-center">{grandTotals.orders}</td>
                  {/* Kids size grand totals */}
                  {kidsFullNames.map(size => (
                    <td key={size} className="td-center">
                      {invoiceData.reduce((s, f) => s + (f.kidsSizes[normalizeSize(size)] || 0), 0)}
                    </td>
                  ))}
                  <td className="td-center">{invoiceData.reduce((s, f) => s + (f.kidsSizes['S'] || 0), 0)}</td>
                  <td className="td-center">{invoiceData.reduce((s, f) => s + (f.kidsSizes['M'] || 0), 0)}</td>
                  <td className="td-total-cell">
                    <span className="qty-badge">{grandTotals.kids}</span>
                    <span className="amt-badge">{formatCurrency(grandTotals.kidsAmt)}</span>
                  </td>
                  {/* V-Neck S-L grand totals */}
                  <td className="td-center td-vneck">{invoiceData.reduce((s, f) => s + f.vneckSML.S, 0)}</td>
                  <td className="td-center td-vneck">{invoiceData.reduce((s, f) => s + f.vneckSML.M, 0)}</td>
                  <td className="td-center td-vneck">{invoiceData.reduce((s, f) => s + f.vneckSML.L, 0)}</td>
                  <td className="td-total-cell td-vneck">
                    <span className="qty-badge">{grandTotals.vsml}</span>
                  </td>
                  <td className="td-amt td-vneck">{formatCurrency(grandTotals.vsmlAmt)}</td>
                  {/* V-Neck XL grand totals */}
                  <td className="td-center td-vxl">{invoiceData.reduce((s, f) => s + f.vneckXL.XL, 0)}</td>
                  <td className="td-center td-vxl">{invoiceData.reduce((s, f) => s + f.vneckXL.XXL, 0)}</td>
                  <td className="td-center td-vxl">{invoiceData.reduce((s, f) => s + f.vneckXL.XXXL, 0)}</td>
                  <td className="td-total-cell td-vxl">
                    <span className="qty-badge">{grandTotals.vxl}</span>
                  </td>
                  <td className="td-amt td-vxl">{formatCurrency(grandTotals.vxlAmt)}</td>
                  {/* Stickers grand total */}
                  <td className="td-center td-stickers">{grandTotals.stickers}</td>
                  <td className="td-amt td-stickers">{formatCurrency(grandTotals.stickerAmt)}</td>
                  {/* Grand invoice total */}
                  <td className="td-invoice-total grand-total">{formatCurrency(grandTotals.invoice)}</td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>

          {/* ── PER-FRANCHISEE CARDS (detail view) ── */}
          <div className="franchisee-cards-section">
            <h2 className="section-heading">📊 Per-Franchisee Detail</h2>
            <div className="franchisee-cards-grid">
              {invoiceData.map(f => (
                <div key={f.name} className="franchisee-invoice-card">
                  <div className="fic-header">
                    <div>
                      <div className="fic-name">{f.name}</div>
                      <div className="fic-orders">{f.orderCount} order{f.orderCount !== 1 ? 's' : ''}: {f.orders.map(o => o.orderNumber || 'N/A').join(', ')}</div>
                    </div>
                    <button
                      className="fic-download-btn"
                      onClick={() => downloadExcelSingle(f)}
                    >
                      ⬇ Download Invoice
                    </button>
                  </div>

                  <div className="fic-sections">
                    {/* Kids */}
                    <div className="fic-section fic-section-kids">
                      <div className="fic-section-title">👕 Kids T-Shirts (Round Neck)</div>
                      <div className="fic-sizes-grid">
                        {allRoundNeck.map(size => {
                          const qty = f.kidsSizes[normalizeSize(size)] || 0;
                          return qty > 0 ? (
                            <div key={size} className="fic-size-pill">
                              <span className="pill-size">{size.replace(' YEARS', '')}</span>
                              <span className="pill-qty">{qty}</span>
                            </div>
                          ) : null;
                        })}
                      </div>
                      <div className="fic-subtotal">
                        <span>{f.kidsTotal} shirts × R{f.KIDS_PRICE}</span>
                        <span className="fic-amt">{formatCurrency(f.kidsAmt)}</span>
                      </div>
                    </div>

                    {/* V-Neck S-L */}
                    {f.vneckSMLTotal > 0 && (
                      <div className="fic-section fic-section-vsml">
                        <div className="fic-section-title">👔 V-Neck S / M / L</div>
                        <div className="fic-sizes-grid">
                          {f.vneckSML.S > 0 && <div className="fic-size-pill"><span className="pill-size">S</span><span className="pill-qty">{f.vneckSML.S}</span></div>}
                          {f.vneckSML.M > 0 && <div className="fic-size-pill"><span className="pill-size">M</span><span className="pill-qty">{f.vneckSML.M}</span></div>}
                          {f.vneckSML.L > 0 && <div className="fic-size-pill"><span className="pill-size">L</span><span className="pill-qty">{f.vneckSML.L}</span></div>}
                        </div>
                        <div className="fic-subtotal">
                          <span>{f.vneckSMLTotal} shirts × R{f.vneckSMLPrice}</span>
                          <span className="fic-amt">{formatCurrency(f.vneckSMLAmt)}</span>
                        </div>
                      </div>
                    )}

                    {/* V-Neck XL */}
                    {f.vneckXLTotal > 0 && (
                      <div className="fic-section fic-section-vxl">
                        <div className="fic-section-title">👔 V-Neck XL / XXL / XXXL</div>
                        <div className="fic-sizes-grid">
                          {f.vneckXL.XL > 0 && <div className="fic-size-pill"><span className="pill-size">XL</span><span className="pill-qty">{f.vneckXL.XL}</span></div>}
                          {f.vneckXL.XXL > 0 && <div className="fic-size-pill"><span className="pill-size">XXL</span><span className="pill-qty">{f.vneckXL.XXL}</span></div>}
                          {f.vneckXL.XXXL > 0 && <div className="fic-size-pill"><span className="pill-size">XXXL</span><span className="pill-qty">{f.vneckXL.XXXL}</span></div>}
                        </div>
                        <div className="fic-subtotal">
                          <span>{f.vneckXLTotal} shirts × R{f.vneckXLPrice}</span>
                          <span className="fic-amt">{formatCurrency(f.vneckXLAmt)}</span>
                        </div>
                      </div>
                    )}

                    {/* Stickers */}
                    {f.stickersQty > 0 && (
                      <div className="fic-section fic-section-stickers">
                        <div className="fic-section-title">🏷 Stickers</div>
                        <div className="fic-subtotal">
                          <span>{f.stickersQty} rolls × R{f.stickerPrice}</span>
                          <span className="fic-amt">{formatCurrency(f.stickerAmt)}</span>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="fic-total-bar">
                    <span>INVOICE TOTAL</span>
                    <span className="fic-total-amt">{formatCurrency(f.invoiceTotal)}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default HOTshirtInvoiceReport;